'use client';

import Box from '@mui/material/Box';
import { alpha, type SxProps, type Theme, useTheme } from '@mui/material/styles';
import { useId } from 'react';
import { Area, AreaChart, ResponsiveContainer } from 'recharts';
import { composeSx } from '../../utils/styledUtils';

export type SparkAccent = 'primary' | 'success' | 'warning' | 'error' | 'info';

export interface SparkAreaProps {
  /**
   * Series data. Either a flat array of numbers (each treated as a point) or an array of objects
   * keyed by `valueKey`.
   */
  data: number[] | Array<Record<string, number>>;
  /** Key to read the value from when data is an array of objects. Default 'value'. */
  valueKey?: string;
  /** Accent color for the line and gradient fill. Default 'success'. */
  accent?: SparkAccent;
  /** Chart height in pixels. Default 48. */
  height?: number;
  /** Line stroke width. Default 1.5. */
  strokeWidth?: number;
  /** Curve type — 'monotone' (smoothed) or 'linear'. Default 'monotone'. */
  curve?: 'monotone' | 'linear';
  /** Hide the gradient fill, show only the line. Default false. */
  lineOnly?: boolean;
  /** Sx overrides for the outer Box. */
  sx?: SxProps<Theme>;
  /** Aria label describing the trend (e.g. "12 week capacity utilized — +9.2 points"). */
  ariaLabel?: string;
}

function normalizeData(data: SparkAreaProps['data'], valueKey: string): Array<Record<string, number>> {
  if (data.length === 0) return [];
  const objects: Array<Record<string, number>> =
    typeof data[0] === 'number'
      ? (data as number[]).map((value, index) => ({ index, [valueKey]: value }))
      : (data as Array<Record<string, number>>);
  // Drop non-finite values (NaN, Infinity) so Recharts doesn't render visual gaps silently.
  return objects.filter((d) => Number.isFinite(d[valueKey]));
}

export function SparkArea({
  data,
  valueKey = 'value',
  accent = 'success',
  height = 48,
  strokeWidth = 1.5,
  curve = 'monotone',
  lineOnly = false,
  sx,
  ariaLabel,
}: SparkAreaProps) {
  const theme = useTheme();
  const gradientId = useId();
  const color = theme.palette[accent].main;

  const normalized = normalizeData(data, valueKey);
  // A single point can't draw a curve; treat as empty so the placeholder renders instead of an invisible chart.
  const isEmpty = normalized.length < 2;

  // Only expose the chart to AT when the caller supplied a descriptive
  // ariaLabel — otherwise a screen reader would announce "graphic" with no
  // name. Decorative sparklines (no label) render as plain visual content.
  const hasAriaLabel = typeof ariaLabel === 'string' && ariaLabel.length > 0;

  return (
    <Box
      {...(hasAriaLabel ? { role: 'img' as const, 'aria-label': ariaLabel } : { 'aria-hidden': true })}
      sx={composeSx({ width: '100%', height }, sx)}
    >
      {isEmpty ? null : (
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={normalized} margin={{ top: 2, right: 2, bottom: 2, left: 2 }}>
            {!lineOnly && (
              <defs>
                <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={color} stopOpacity={0.4} />
                  <stop offset="100%" stopColor={color} stopOpacity={0} />
                </linearGradient>
              </defs>
            )}
            <Area
              type={curve}
              dataKey={valueKey}
              stroke={color}
              strokeWidth={strokeWidth}
              fill={lineOnly ? 'transparent' : `url(#${gradientId})`}
              fillOpacity={lineOnly ? 0 : 1}
              isAnimationActive={false}
              dot={false}
              activeDot={false}
            />
          </AreaChart>
        </ResponsiveContainer>
      )}
      {isEmpty && (
        <Box
          aria-hidden
          sx={{
            width: '100%',
            height: '100%',
            borderRadius: 0.5,
            backgroundColor: alpha(color, 0.04),
          }}
        />
      )}
    </Box>
  );
}

SparkArea.displayName = 'SparkArea';

export default SparkArea;
